<template>
  <sp-menu
    class="results-go-to-date"
    :class="classModifiers"
    :label="resultText('go_to_date')"
    location="bottom"
    close-on-content-click="false"
    no-sheet
    :open="visible"
    @update-open="handleUpdateOpen"
  >
    <sp-button
      slot="activator"
      class="results-go-to-date__trigger"
      :data-testid="TestId.finder.goToDate"
      variant="text"
      densitysmcompact="false"
      @click="trackControlClick"
    >
      <sp-icon slot="prepend" class="results-go-to-date__leading-icon" name="event-filled"></sp-icon>
      {{ resultText("go_to_date") }}
      <sp-caret class="results-go-to-date__caret" variant="filled" :active="visible" size="small"></sp-caret>
    </sp-button>
    <sp-date-picker
      ref="calendar"
      v-preserve-properties="propertyBindings(calendarBindings, datePickerProps)"
      class="results-go-to-date__calendar"
      inline
      :aria-label="resultText('select_a_date')"
      v-bind="attributeBindings(calendarBindings, datePickerProps)"
      :value.attr="initialValue"
      @change="selectDate"
      @visible-range="recordVisibleRange"
    ></sp-date-picker>
  </sp-menu>
</template>

<script setup>
import { TestId } from "#/test-ids";
import { computed, ref, shallowRef, useTemplateRef, watch } from "vue";
import { format } from "date-fns";
import { props as datePickerProps } from "#ce/components/SpDatePicker/props";
import { attributeBindings, propertyBindings, vPreserveProperties } from "#ce/utils/property-bindings";
import { dayStates } from "#ce/apps/SpFinderAppointmentDateSelector/lib/day-states";
import { resultText } from "#ce/components/SpFinder/lib/result-text";
import { trackControlClick } from "#ce/components/SpFinder/lib/click-analytics";

const props = defineProps({
  startDate: {
    type: String,
    default: undefined,
  },
  requestKey: {
    type: String,
    default: undefined,
  },
  availability: {
    type: Function,
    required: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  divided: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select-date"]);

const calendar = useTemplateRef("calendar");
const visible = ref(false);
const visibleRange = shallowRef();
const states = shallowRef({});
const today = format(new Date(), "yyyy-MM-dd");
const initialValue = computed(() => props.startDate);
const openToDate = computed(() => props.startDate ?? today);
let availabilityRequest = 0;

const classModifiers = computed(() => ({
  "--active": visible.value,
  "--mobile": props.mobile,
  "--divided": props.divided,
}));
const calendarBindings = computed(() => ({
  "min-date": today,
  "open-to-date": openToDate.value,
  "day-states": states.value,
}));

// Only the latest request's reply is shown, so a slow reply for an earlier month is dropped.
async function renderAvailability() {
  if (!visible.value || !visibleRange.value) {
    return;
  }
  const request = ++availabilityRequest;
  let availability;
  try {
    availability = await props.availability({
      startDate: visibleRange.value.start,
      endDate: visibleRange.value.end,
    });
  } catch {
    availability = {};
  }
  if (request === availabilityRequest) {
    states.value = dayStates(availability);
  }
}

function show() {
  visible.value = true;
  calendar.value?.focus();
  renderAvailability();
}

function hide() {
  visible.value = false;
}

function handleUpdateOpen(event) {
  if (event.detail?.[0]) {
    show();
  } else {
    hide();
  }
}

function selectDate(event) {
  const date = event.currentTarget.value;
  hide();
  emit("select-date", date);
}

function recordVisibleRange({ detail }) {
  visibleRange.value = detail[0];
  renderAvailability();
}

// A new search outdates every reply in flight and the states shown, open or closed.
function renderSearchAvailability() {
  availabilityRequest += 1;
  states.value = {};
  renderAvailability();
}

watch(() => props.requestKey, renderSearchAvailability);

defineExpose({ hide });
</script>

<style lang="scss" scoped>
.results-go-to-date {
  --trigger-background: var(--sp-comp-finder-results-go-to-date-trigger-background, #fff);
  --trigger-border: var(--sp-comp-finder-results-go-to-date-trigger-border, 1px solid #dadada);
  --trigger-radius: var(--sp-comp-finder-results-go-to-date-trigger-border-radius, 6px);
  --trigger-shadow: var(--sp-comp-finder-results-go-to-date-trigger-shadow, 0 1px 3px 0 rgb(0 0 0 / 10%));
  --trigger-padding: var(--sp-comp-finder-results-go-to-date-trigger-padding, 4px 16px);
  --trigger-color: var(--sp-comp-finder-results-go-to-date-trigger-color, var(--sp-sys-color-primary));
  --trigger-font-weight: var(--sp-comp-finder-results-go-to-date-trigger-font-weight, normal);
  --trigger-font-size: var(--sp-comp-finder-results-go-to-date-trigger-font-size, 1em);
  --mobile-divider: var(--sp-comp-finder-results-go-to-date-divider-mobile, 1px solid #c6d2d7);
  --calendar-padding: var(--sp-comp-finder-results-go-to-date-calendar-padding, 40px 45px);
  --calendar-border: var(--sp-comp-finder-results-go-to-date-calendar-border, 1px solid #c6d2d7);

  display: block;
  width: fit-content;
  margin: 0 auto;

  &__trigger {
    --sp-comp-button-size: auto;
    --sp-comp-button-padding: var(--trigger-padding);
    --sp-comp-button-gap: 4px;
    --sp-comp-button-border-radius: var(--trigger-radius);
    --sp-comp-button-font-short: var(--trigger-font-weight) var(--trigger-font-size) /
      var(--sp-comp-button-line-height, var(--sp-sys-typescale-body-line-height))
      var(--sp-comp-button-font-family, var(--sp-sys-typescale-body-font-family));
    --sp-comp-button-primary-variant-text-text-color: var(--trigger-color);
    --sp-comp-button-primary-hover-variant-text-bg-opacity: 0;

    border: var(--trigger-border);
    border-radius: var(--trigger-radius);
    background-color: var(--trigger-background);
    box-shadow: var(--trigger-shadow);
  }

  // The caret icon pads its glyph, so its box is trimmed to sit and size like the legacy caret glyph.
  &__caret {
    margin-inline: calc(var(--sp-ref-scale-4) * -1) calc(var(--sp-ref-scale-6) * -1);
  }

  &__leading-icon {
    --sp-comp-icon-size: 22px;

    display: var(--sp-comp-finder-results-go-to-date-leading-icon-display, block);

    transform: translateY(-1px);
  }

  &__calendar {
    display: block;
    box-sizing: border-box;
    padding: var(--calendar-padding);
    border: var(--calendar-border);
    background: var(--sp-sys-color-surface);
    box-shadow: 0 1px 1px 1px rgb(0 0 0 / 5%);
  }

  &.--mobile.--divided {
    padding-inline: var(--sp-ref-scale-10);
    border-inline-start: var(--mobile-divider);
  }

  &.--mobile &__trigger {
    --sp-comp-button-padding: 0;

    border: 0;
    background: transparent;
    box-shadow: none;
  }

  &.--mobile &__leading-icon,
  &.--mobile &__caret {
    display: none;
  }

  &.--mobile &__calendar {
    --calendar-padding: 30px 20px;

    width: 350px;
  }
}
</style>
